<template>
  <div class="view" v-if="canView || canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('common.partners')"
      :grid-ref="$refs.smPartnersGrid"
      :show-add="canEdit"
      :show-filter="false"
      @add="newObject"
      :show-refresh="true"
    />

    <DxDataGrid
      ref="smPartnersGrid"
      :data-source="smPartnersDataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
      @editing-start="onEditingStart"
      @exporting="onExporting"
      @option-changed="onOptionChanged"
    >
      <DxColumn
        data-field="name"
        :caption="$t('common.name')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :min-width="250"
      />
      <DxColumn
        data-field="legal_form"
        :caption="$t('stock_management.partners.legal_form')"
        cell-template="legalFormViewTemplate"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :allow-filtering="true"
        :min-width="180"
        :calculateCellValue="getLegalFormName"
      >
        <DxLookup :data-source="partnerLegalForms" valueExpr="uuid" displayExpr="name" />
      </DxColumn>
      <template #legalFormViewTemplate="{ data }">
        <span>
          {{ getLegalFormName(data.data) }}
        </span>
      </template>
      <DxColumn
        data-field="id_number"
        :caption="$t('stock_management.partners.id_number')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="160"
      />
      <DxColumn
        data-field="nr_reg"
        :caption="$t('common.nr_reg')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="160"
      />
      <DxColumn
        data-field="is_active"
        :caption="$t('common.active')"
        data-type="number"
        :allow-sorting="true"
        :allow-filtering="true"
        cell-template="isActiveViewTemplate"
        :min-width="140"
        :calculateCellValue="getActiveStatusName"
      >
        <DxLookup :data-source="activeLookup" valueExpr="value" displayExpr="text" />
      </DxColumn>
      <template #isActiveViewTemplate="{ data }">
        <span>
          {{ getActiveStatusName(data.data) }}
        </span>
      </template>
      <DxColumn
        data-field="created_at"
        :caption="$t('common.created_at')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="getDxGridDefaultDateFormat"
      />
      <DxColumn
        data-field="updated_at"
        :caption="$t('common.last_update')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="updatedAtDateTimeFormat"
      />
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.smPartnersGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :edit-action="onEdit"
        />
      </template>

      <DxEditing mode="row" :allow-updating="false" :allow-adding="false" :allow-deleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('common.partners'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxEditing,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
  DxTexts,
  DxLookup,
} from "devextreme-vue/data-grid";
import { canDelete, canEdit, canView } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import { only_company_identification_number } from "@/helpers/common";
import { handleError } from "@/helpers/generalActions";

export default {
  name: "SMPartnerList",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      filterPanelVisible: false,
      smPartnersDataSource: new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: async (loadOptions) => {
            const dataSource = {
              data: [],
              totalCount: 0,
            };

            try {
              const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
              const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;
              const payload = {
                page: pageNumber,
                limit: pageLimit,
                filter: loadOptions.filter,
                sort: loadOptions.sort,
                stop_to_set_just_active: true,
              };

              const response = await this.getSmPartners(payload);
              dataSource.data = response?.data ?? [];
              dataSource.totalCount = response?.pagination?.total ?? 0;
              return dataSource;
            } catch (error) {
              console.error("Error fetching partners:", error);
              throw error;
            }
          },
          remove: this.onDelete,
        }),
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
    };
  },
  components: {
    Forbidden,
    GridPanelHeader,
    GridFilter,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxEditing,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
    DxActionMenu,
    DxTexts,
    DxLookup,
  },
  computed: {
    ...mapGetters({
      partnerLegalForms: "smPartners/smPartnerLegalForms",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid sm-partners-grid",
      };
    },
    activeLookup() {
      return [
        { value: 1, text: this.$t("common.active") },
        { value: 0, text: this.$t("common.inactive") },
      ];
    },
    updatedAtDateTimeFormat() {
      return this.getDxGridDefaultDateFormat ? `${this.getDxGridDefaultDateFormat} HH:mm` : "yyyy-MM-dd HH:mm";
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getSmPartners: "smPartners/getSmPartners",
      deleteSmPartner: "smPartners/deleteSmPartner",
      getSmPartnerLegalForms: "smPartners/getSmPartnerLegalForms",
    }),
    newObject() {
      this.$router.push({
        path: "/stock-management/sm-partners/add",
      });
    },
    onEditingStart(e) {
      e.cancel = true;
      if (e && e.data) {
        this.onEdit(e.data);
      }
    },
    onEdit(row) {
      if (!row || !row.uuid) return;
      this.$router.push({ path: `/stock-management/sm-partners/${row.uuid}` });
    },
    async onDelete(key) {
      if (!key || !this.canDelete) return;

      try {
        await this.deleteSmPartner({ uuid: key });
        this.reloadGrid();
      } catch (e) {
        handleError(this, e);
      }
    },
    reloadGrid() {
      const instance = this.$refs?.smPartnersGrid?.instance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
    },
    onExporting(e) {
      e.fileName = "SMPartners_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SMPartners");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            // Skip processing for non-data rows (headers, summaries, etc.)
            if (!gridCell?.data) {
              return;
            }

            if (gridCell?.column?.dataField === "legal_form") {
              excelCell.value = this.getLegalFormName(gridCell?.data);
            } else if (gridCell?.column?.dataField === "is_active") {
              excelCell.value = this.getActiveStatusName(gridCell?.data);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${e.fileName}.xlsx`);
          });
        });
      }
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.smPartnersGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
        this.reloadGrid();
      }
    },
    onlyCompanyIdentificationNumber(event) {
      return only_company_identification_number(event);
    },
    getLegalFormName(rowData) {
      const type = this.partnerLegalForms.find((item) => item.uuid === rowData.legal_form);
      return type ? type.name : null;
    },
    getActiveStatusName(rowData) {
      const status = this.activeLookup.find((item) => item.value === rowData.is_active);
      return status ? status.text : null;
    },
  },
  mounted() {
    this.getSmPartnerLegalForms();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";
</style>
